import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import BannerCreative from './BannerCreative';

const AUTO_ADVANCE_MS = 6000;

/**
 * Rotating carousel of banners at the top of the public jobs page (spec v2 §4/§6).
 *
 * v2: slides render through the shared <BannerCreative> (same component as every
 * wizard/edit preview → preview === live), the box is content-width-friendly
 * (rounded, full border — the parent in Jobs.js constrains width), taller
 * (160px mobile / 280px desktop), and the feed is mixed-kind:
 *  - employer banners → navigate to the job with ?ref_banner={id} (click→apply
 *    attribution, spec §12) after a fire-and-forget click ping.
 *  - platform banners (admin, kind='platform') → link_url target: internal paths go
 *    through the SPA router; absolute http(s) URLs open in a new tab with
 *    noopener,noreferrer. No report affordance on platform banners.
 *
 * Fetches GET /api/banners/active on mount. Renders NOTHING while loading, on a
 * fetch error, or with 0 banners — `onBannersChange(hasBanners)` lets Jobs.js show
 * the Trust Bar instead (the hero slot is never blank).
 *
 * Auto-advances every ~6s, pauses on hover, and skips auto-advance entirely for
 * prefers-reduced-motion (manual dots/prev/next still work).
 */
const BannerCarousel = ({ onBannersChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [reportedIds, setReportedIds] = useState(() => new Set());
  const onBannersChangeRef = useRef(onBannersChange);
  onBannersChangeRef.current = onBannersChange;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const res = await api.get('/api/banners/active', { signal: controller.signal });
        if (cancelled) return;
        const list = Array.isArray(res.data?.banners)
          ? res.data.banners
          : Array.isArray(res.data)
            ? res.data
            : [];
        setBanners(list);
        if (onBannersChangeRef.current) onBannersChangeRef.current(list.length > 0);
      } catch (err) {
        if (cancelled) return;
        console.error('[BannerCarousel] Failed to fetch active banners:', err);
        setBanners([]);
        if (onBannersChangeRef.current) onBannersChangeRef.current(false);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // Auto-advance rotation. Paused on hover; skipped entirely for prefers-reduced-motion.
  useEffect(() => {
    if (banners.length < 2 || isHovered) return undefined;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return undefined;

    const id = setInterval(() => {
      setIndex((i) => (i + 1) % banners.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [banners.length, isHovered]);

  // If the feed shrinks (e.g. a re-fetch), keep the index in range.
  useEffect(() => {
    if (banners.length > 0 && index >= banners.length) setIndex(0);
  }, [banners.length, index]);

  if (!loaded || banners.length === 0) return null;

  const current = banners[index];
  if (!current) return null;

  const goTo = (i) => setIndex(((i % banners.length) + banners.length) % banners.length);
  const handlePrev = (e) => { e.stopPropagation(); goTo(index - 1); };
  const handleNext = (e) => { e.stopPropagation(); goTo(index + 1); };

  // Same-origin absolute URLs (e.g. https://www.boholtrabaho.com/peso/panglao) are
  // INTERNAL — route them through the SPA instead of a full page load / new tab.
  const target = typeof current.target === 'string' ? current.target : '';
  const isAbsolute = /^https?:\/\//i.test(target);
  const sameOriginPath = isAbsolute && target.startsWith(window.location.origin)
    ? (target.slice(window.location.origin.length) || '/')
    : null;
  const internalPath = !isAbsolute ? target : sameOriginPath;

  const handleSlideClick = () => {
    if (current.id) {
      api.post(`/api/banners/${current.id}/click`).catch(() => {}); // fire-and-forget
    }
    if (!target) return;
    if (current.open_new_tab) {
      // Admin chose "open in a new tab" for this banner — never leak opener/referrer.
      window.open(target, '_blank', 'noopener,noreferrer');
    } else if (internalPath) {
      if (current.kind === 'employer') {
        // Carry the banner id so an apply on this job attributes to the banner (§12).
        const sep = internalPath.includes('?') ? '&' : '?';
        navigate(`${internalPath}${sep}ref_banner=${current.id}`);
      } else {
        navigate(internalPath);
      }
    } else {
      // Off-site link with same-tab preference: full navigation in this tab.
      window.location.assign(target);
    }
  };

  const handleSlideKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSlideClick();
    }
  };

  const handleReport = (e) => {
    e.stopPropagation();
    if (!current.id || reportedIds.has(current.id)) return;
    setReportedIds((prev) => new Set(prev).add(current.id));
    api.post(`/api/banners/${current.id}/report`).catch(() => {});
    toast.success(t('banner.reportSuccess', 'Thanks — this banner has been reported for review.'));
  };

  // Feed fields are plain text (length-bounded server-side); React escapes on render.
  const label = typeof current.label === 'string' ? current.label.trim() : '';
  const ariaLabel = current.cta === 'learn'
    ? (label
      ? t('banner.learnMoreAbout', { name: label, defaultValue: 'Learn more about {{name}}' })
      : t('banner.learnMore', 'Learn more'))
    : (label
      ? t('banner.viewJobFrom', { name: label, defaultValue: 'View job from {{name}}' })
      : t('banner.viewSponsoredJob', 'View sponsored job'));

  return (
    <div
      className="relative w-full h-[160px] md:h-[280px] overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="banner-carousel"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleSlideClick}
        onKeyDown={handleSlideKeyDown}
        className="absolute inset-0 cursor-pointer"
        aria-label={ariaLabel}
      >
        <BannerCreative
          imageUrl={current.image_url}
          imagePosition={current.image_position}
          headline={current.headline}
          detail={current.detail}
          label={label}
          tag={current.tag}
          cta={current.cta}
          showReport={!!current.show_report}
          isReported={reportedIds.has(current.id)}
          onReport={handleReport}
        />
      </div>

      {banners.length > 1 && (
        <>
          <button
            type="button"
            onClick={handlePrev}
            aria-label={t('banner.previous', 'Previous banner')}
            className="absolute left-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label={t('banner.next', 'Next banner')}
            className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <ChevronRight size={16} />
          </button>

          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {banners.map((b, i) => (
              <button
                key={b.id}
                type="button"
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                aria-label={t('banner.goToSlide', { index: i + 1, defaultValue: 'Go to banner {{index}}' })}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BannerCarousel;
